/**
 * How AI agents and GenAI applications read across the app (task 25233).
 *
 * An actor recorded as `type: "ai_agent"` shows its normalized display name —
 * "Co Pilot" for Microsoft's Copilot — with "AI Agent" beneath it, and never
 * its address: the recorded "microsoft@copilot.com" is not a person to email.
 * Human actors are left exactly as they were.
 */

export const AI_AGENT_LABEL = "AI Agent";

export const is_ai_agent_entity = (entity: any): boolean =>
  !!entity && String(entity.type || "").toLowerCase() === "ai_agent";

/** "Copilot", "Microsoft 365 Copilot", "Co-Pilot" -> "Co Pilot"; none -> "AI Agent" */
export const ai_agent_display_name = (name?: string | null): string => {
  const n = String(name || "").trim();
  if (!n) return AI_AGENT_LABEL;
  if (/^(microsoft\s*(365\s*)?)?co[\s-]?pilot$/i.test(n)) return "Co Pilot";
  return n;
};

/**
 * An entity as it should be shown: AI agents get their display name, no
 * address and an `is_ai_agent` flag; everyone else is returned unchanged, so
 * the direction the backend gave (exposed by / exposed to) is kept as is.
 */
export const as_displayed_actor = <T extends Record<string, any>>(entity: T): T => {
  if (!is_ai_agent_entity(entity)) return entity;
  return { ...entity, name: ai_agent_display_name(entity.name), email: "", is_ai_agent: true };
};

/** GenAI applications as they are named in tooltips */
const GENAI_APP_NAMES: Record<string, string> = {
  copilot: "Microsoft 365 Copilot",
  m365copilot: "Microsoft 365 Copilot",
  microsoft365copilot: "Microsoft 365 Copilot",
  microsoftcopilot: "Microsoft Copilot",
  chatgpt: "ChatGPT",
  openai: "OpenAI",
  gemini: "Gemini",
};

const key_of = (source: string) => String(source || "").toLowerCase().replace(/[^a-z0-9]/g, "");

/** a GenAI application: by its own name, or because the event is an AI interaction */
export const is_genai_app = (source?: string | null, event_type?: string | null): boolean =>
  /copilot|chatgpt|openai|gemini|genai/.test(key_of(source || "")) ||
  String(event_type || "") === "AI Interaction";

/** the application's display name for its tooltip; "AI application" when none is recorded */
export const genai_app_name = (source?: string | null): string => {
  const key = key_of(source || "");
  if (!key) return "AI application";
  if (GENAI_APP_NAMES[key]) return GENAI_APP_NAMES[key];
  return String(source)
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};
